import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useBlobFileStorage } from 'components/core/BlobFileStorage';
import { downloadFile } from 'services/fileOperations';
import { Spinner } from 'components/core';
import { useGalleryContext } from '../../';
import { identitySelector } from '../../selectors';
import { createUniqueId } from 'shared/utils/id/id';
import { ImageDisplay } from '../imageDisplay';
import { WordDisplay } from '../wordDisplay';
import { ExcelDisplay } from '../excelDisplay';
import { PdfDisplay } from '../pdfDisplay';
import { FilePreviewError } from '../filePreviewError';
import { showGenericErrorToast } from 'services/toaster';
import { signedRequestService, thumbnailService } from 'services/fileService';
import { GALLERY_FILE_TYPE } from '../../fileKindRoutingModel';
import { buildFilePreviewKey } from './model';
import { resolveFilePreview } from './resolveFilePreviewModel';
import { FileDisplayProps } from './types';
import { spinnerContainerSx } from './styles';

const FileDisplay: React.FC<FileDisplayProps> = (props) => {
  const galleryContext = useGalleryContext();
  const [file] = galleryContext.useMainDisplayedFile(identitySelector);
  const { ImageDisplayOverlay, rightPanelControls } = galleryContext;
  const blobFileStorage = useBlobFileStorage();
  const { drawing } = props;

  const storedBlobUrl = thumbnailService.resolveStoredBlobUrl(
    drawing,
    file,
    blobFileStorage.getItem
  );

  const handleDownloadFileClick = useCallback(() => {
    if (!file) {
      return;
    }

    const resolvedDownloadSrc = signedRequestService.resolveGalleryDownloadSource(
      file,
      storedBlobUrl || drawing?.mergedImageUrl
    );
    downloadFile(
      resolvedDownloadSrc,
      file.title,
      file.data?.extension
    ).catch(() => showGenericErrorToast());
  }, [file, storedBlobUrl, drawing?.mergedImageUrl]);

  const key = useMemo(() => {
    return createUniqueId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const [rightPanelVisible, setRightPanelVisible] = useState(
    rightPanelControls?.getVisibleStore().get()
  );

  useEffect(() => {
    if (!rightPanelControls) return;
    const action = (): void =>
      setRightPanelVisible(rightPanelControls.getVisibleStore().get());
    action();
    return rightPanelControls.getVisibleStore().subscribe(action);
  }, [rightPanelControls]);

  if (!file) {
    return null;
  }

  const resolution = resolveFilePreview(file, { storedBlobUrl, drawing });

  switch (resolution.kind) {
    case 'loading':
      return (
        <Box sx={spinnerContainerSx}>
          <Spinner reason={resolution.reason} />
        </Box>
      );
    case 'error':
      return <FilePreviewError onDownload={handleDownloadFileClick} />;
    case 'video':
      return (
        <div className='file-preview'>
          <video key={key} crossOrigin='use-credentials' controls>
            <source src={resolution.source} type={resolution.mimeType} />
          </video>
        </div>
      );
    case 'image': {
      const imageKey = buildFilePreviewKey(
        file,
        resolution.source,
        rightPanelVisible
      );
      return (
        <ImageDisplay
          key={imageKey}
          source={resolution.source}
          imageRotation={file.imageRotation}
          alt={file.description || file.title || ''}
          ImageDisplayOverlay={ImageDisplayOverlay}
        />
      );
    }
    case GALLERY_FILE_TYPE.PDF: {
      const pdfKey = buildFilePreviewKey(
        file,
        resolution.source,
        rightPanelVisible
      );
      return (
        <div className='file-preview'>
          <PdfDisplay
            key={pdfKey}
            src={resolution.source}
            onDownload={handleDownloadFileClick}
          />
        </div>
      );
    }
    case 'word':
      return (
        <div className='file-preview'>
          <WordDisplay
            src={resolution.source}
            onDownload={handleDownloadFileClick}
          />
        </div>
      );
    case 'excel':
      return (
        <div className='file-preview'>
          <ExcelDisplay
            src={resolution.source}
            onDownload={handleDownloadFileClick}
          />
        </div>
      );
  }
};

export default FileDisplay;
